import type { RouterClient } from "@orpc/server";
import type { AppRouter } from "@shared/rpc-types";
import { useQuery } from "@tanstack/react-query";
import {
  Briefcase,
  Calendar,
  CheckCircle2,
  ClipboardList,
  Clock,
  ExternalLink,
  Pencil,
  RefreshCw,
  Send,
  X,
} from "lucide-react";
import { useState } from "react";
import { Link } from "wouter";
import { ApplicantDetailModal } from "@/components/applicant-detail-modal";
import { CancelInterviewDialog } from "@/components/cancel-interview-dialog";
import { InfoCard } from "@/components/info-card";
import {
  type InterviewFeedbackData,
  InterviewFeedbackDialog,
} from "@/components/interview-feedback-dialog";
import { PageHeader } from "@/components/page-header";
import { RescheduleConfirmDialog } from "@/components/reschedule-confirm-dialog";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useCancelInterview } from "@/hooks/use-cancel-interview";
import { useQueryParams } from "@/hooks/use-query-params";
import { useRescheduleInterview } from "@/hooks/use-reschedule-interview";
import { formatDateTime, getDepartmentLabel, getJobLevelLabel } from "@/lib/constants";
import { orpc } from "@/lib/rpc";

type InterviewWithDetails = Awaited<
  ReturnType<RouterClient<AppRouter>["interviews"]["mine"]>
>[number];

export default function MyInterviews() {
  const { getParam, setParam } = useQueryParams();
  const activeTab = getParam("tab") || "pending";
  const [detailApplicationId, setDetailApplicationId] = useState<string | null>(null);
  const [feedbackDialog, setFeedbackDialog] = useState(false);
  const [feedbackInterview, setFeedbackInterview] = useState<InterviewFeedbackData | null>(null);
  const [isEditingFeedback, setIsEditingFeedback] = useState(false);

  const reschedule = useRescheduleInterview();
  const cancel = useCancelInterview();

  const { data: interviews, isLoading } = useQuery(orpc.interviews.mine.queryOptions());

  const isMyFeedbackDone = (i: InterviewWithDetails) =>
    !!i.gcaVerdict || !!i.recommendation || !!i.hasMyFeedback;
  const pendingInterviews = interviews?.filter((i) => !isMyFeedbackDone(i)) ?? [];
  const completedInterviews = interviews?.filter((i) => isMyFeedbackDone(i)) ?? [];

  const handleOpenFeedback = (interview: InterviewWithDetails, editing = false) => {
    const fb = editing && interview.myFeedback ? interview.myFeedback : null;
    setFeedbackInterview({
      interviewId: interview.id,
      interviewStageId: interview.interviewStageId,
      interviewerGroupId: interview.interviewerGroupId,
      applicantName: interview.application?.applicant?.fullName,
      stageName: interview.interviewStage?.stageName,
      jobOpeningId: interview.application?.jobOpening?.id,
      jobOpeningTitle: interview.application?.jobOpening?.title,
      applicationId: interview.applicationId,
      allowAutoAdvance: interview.application?.jobOpening?.allowAutoAdvance !== false,
      firefliesRecordingUrl: interview.firefliesRecordingUrl,
      meetingLink: interview.meetingLink,
      gcaVerdict: (fb?.gcaVerdict ?? interview.gcaVerdict) as any,
      gcaNotes: fb?.gcaNotes ?? interview.gcaNotes ?? "",
      roleKnowledgeVerdict: (fb?.roleKnowledgeVerdict ?? interview.roleKnowledgeVerdict) as any,
      roleKnowledgeNotes: fb?.roleKnowledgeNotes ?? interview.roleKnowledgeNotes ?? "",
      compWayVerdict: (fb?.compWayVerdict ?? interview.compWayVerdict) as any,
      compWayNotes: fb?.compWayNotes ?? interview.compWayNotes ?? "",
      generalComments: fb?.generalComments ?? interview.generalComments ?? "",
      notesForNextInterviewer:
        fb?.notesForNextInterviewer ?? interview.notesForNextInterviewer ?? "",
      recommendation: fb?.recommendation ?? interview.recommendation,
      customCriteriaScores: fb?.customCriteriaScores ?? interview.customCriteriaScores ?? null,
    });
    setIsEditingFeedback(editing);
    setFeedbackDialog(true);
  };

  const getStatusKey = (interview: InterviewWithDetails): string => {
    const status = interview.status || "pending";
    if (status === "pending") return "pending";
    if (status === "scheduled") return "scheduled";
    if (interview.recommendation || !!interview.gcaVerdict || interview.hasMyFeedback) {
      return "completed";
    }
    return "awaiting_review";
  };

  const InterviewCard = ({ interview }: { interview: InterviewWithDetails }) => {
    const interviewStatus = interview.status || "pending";
    const hasFeedback =
      !!interview.gcaVerdict || !!interview.recommendation || !!interview.hasMyFeedback;
    const applicant = interview.application?.applicant;
    const jobOpening = interview.application?.jobOpening;
    const stage = interview.interviewStage;
    const statusKey = getStatusKey(interview);
    const needsFeedback = interviewStatus === "finished" && !hasFeedback;

    const jobDescription =
      [
        jobOpening?.department && getDepartmentLabel(jobOpening.department),
        jobOpening?.jobLevel && getJobLevelLabel(jobOpening.jobLevel),
      ]
        .filter(Boolean)
        .join(" · ") || undefined;

    const actionsContent =
      interviewStatus === "scheduled" ? (
        <div className="flex items-center gap-1.5">
          <Button
            size="sm"
            variant="outline"
            data-testid={`button-reschedule-${interview.id}`}
            onClick={(e) => {
              e.stopPropagation();
              reschedule.openReschedule(interview.id, !!interview.interviewerGroupId);
            }}
            className="gap-1.5"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Reagendar
          </Button>
          <Button
            size="sm"
            variant="outline"
            data-testid={`button-cancel-${interview.id}`}
            onClick={(e) => {
              e.stopPropagation();
              cancel.openCancel(interview.id, !!interview.interviewerGroupId);
            }}
            className="gap-1.5 text-destructive hover:text-destructive"
          >
            <X className="h-3.5 w-3.5" />
            Cancelar
          </Button>
        </div>
      ) : needsFeedback ? (
        <div className="flex items-center gap-2">
          {interview.firefliesRecordingUrl && (
            <span
              className="text-[11px] font-semibold text-[hsl(var(--ink-muted))]"
              data-testid={`badge-transcript-${interview.id}`}
            >
              Transcrição disponível
            </span>
          )}
          <Button
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenFeedback(interview);
            }}
            className="gap-1.5"
            data-testid={`button-feedback-${interview.id}`}
          >
            <Send className="h-3.5 w-3.5" />
            Avaliar entrevista
          </Button>
        </div>
      ) : hasFeedback ? (
        <div className="flex items-center gap-2">
          {interview.recommendation ? (
            <span
              className={`text-[13px] font-semibold ${interview.recommendation === "advance" ? "text-[hsl(var(--forest-pulse))]" : "text-[hsl(var(--ink-muted))]"}`}
            >
              {interview.recommendation === "advance" ? "Recomenda" : "Não recomenda"}
            </span>
          ) : interview.hasMyFeedback && interview.myFeedback?.recommendation ? (
            <span
              className={`text-[13px] font-semibold ${interview.myFeedback.recommendation === "advance" ? "text-[hsl(var(--forest-pulse))]" : "text-[hsl(var(--ink-muted))]"}`}
            >
              {interview.myFeedback.recommendation === "advance" ? "Recomenda" : "Não recomenda"}
            </span>
          ) : (
            <span className="text-[13px] font-semibold text-[hsl(var(--ink-muted))]">
              Avaliação enviada
            </span>
          )}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenFeedback(interview, true);
                  }}
                  data-testid={`button-edit-feedback-${interview.id}`}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p className="text-xs">Editar avaliação</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      ) : null;

    return (
      <InfoCard
        title={applicant?.fullName || "Candidato"}
        titleAddon={
          jobOpening && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    href={`/jobs/${jobOpening.id}?tab=kanban&applicationId=${interview.applicationId}`}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  >
                    <ExternalLink
                      className="h-3.5 w-3.5 text-muted-foreground/60 hover:text-muted-foreground transition-colors"
                      data-testid={`link-kanban-${interview.id}`}
                    />
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p className="text-xs">Ver no Kanban</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )
        }
        subtitle={jobOpening?.title || "Vaga"}
        description={jobDescription}
        statusBadge={<StatusBadge status={statusKey} className="flex-shrink-0" />}
        onClick={() => setDetailApplicationId(interview.applicationId)}
        data-testid={`card-interview-${interview.id}`}
        metadata={
          <>
            {stage && (
              <div className="flex items-center gap-1.5">
                <Briefcase className="h-3 w-3" />
                <span>{stage.stageName}</span>
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <Calendar className="h-3 w-3" />
              <span>
                {(interview.status || "pending") === "pending"
                  ? "Aguardando agendamento"
                  : formatDateTime(interview.scheduledAt)}
              </span>
            </div>
          </>
        }
        actions={actionsContent}
      />
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-2 p-6">
        {["a", "b", "c", "d", "e"].map((k) => (
          <div key={k} className="flex items-center gap-3 py-2">
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--sand-divider))]" />
            <span className="h-4 flex-1 max-w-md rounded bg-[hsl(var(--sand-divider))]" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Minhas Entrevistas"
        description="Gerencie as entrevistas que você precisa conduzir"
      />

      <Tabs value={activeTab} onValueChange={(tab) => setParam("tab", tab)} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger
            value="pending"
            className="flex items-center gap-2"
            data-testid="tab-pending"
          >
            <Clock className="h-4 w-4" />
            Pendentes ({pendingInterviews.length})
          </TabsTrigger>
          <TabsTrigger
            value="completed"
            className="flex items-center gap-2"
            data-testid="tab-completed"
          >
            <CheckCircle2 className="h-4 w-4" />
            Concluídas ({completedInterviews.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-4">
          {pendingInterviews.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <div className="rounded-full bg-muted p-4 mb-4">
                  <ClipboardList className="h-8 w-8 text-muted-foreground/60" />
                </div>
                <p className="text-sm font-medium text-foreground mb-1">
                  Nenhuma entrevista pendente
                </p>
                <p className="text-xs text-muted-foreground text-center">
                  Quando você tiver entrevistas para conduzir, elas aparecerão aqui.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {pendingInterviews.map((interview) => (
                <InterviewCard key={interview.id} interview={interview} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="completed" className="mt-4">
          {completedInterviews.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <div className="rounded-full bg-muted p-4 mb-4">
                  <CheckCircle2 className="h-8 w-8 text-muted-foreground/60" />
                </div>
                <p className="text-sm font-medium text-foreground mb-1">
                  Nenhuma entrevista concluída
                </p>
                <p className="text-xs text-muted-foreground text-center">
                  Suas entrevistas concluídas aparecerão aqui.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {completedInterviews.map((interview) => (
                <InterviewCard key={interview.id} interview={interview} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <ApplicantDetailModal
        applicationId={detailApplicationId}
        open={!!detailApplicationId}
        onOpenChange={(open) => {
          if (!open) setDetailApplicationId(null);
        }}
        propertyDefinitions={[]}
        propertyValues={[]}
      />

      <InterviewFeedbackDialog
        open={feedbackDialog}
        onOpenChange={(open) => {
          setFeedbackDialog(open);
          if (!open) setIsEditingFeedback(false);
        }}
        interview={feedbackInterview}
        isEditing={isEditingFeedback}
      />

      <RescheduleConfirmDialog
        open={!!reschedule.rescheduleInterviewId}
        onOpenChange={(open) => {
          if (!open) reschedule.closeReschedule();
        }}
        isPending={reschedule.isPending}
        isGroupInterview={reschedule.isGroupInterview}
        onConfirm={reschedule.confirmReschedule}
        onCancel={reschedule.closeReschedule}
      />

      <CancelInterviewDialog
        open={!!cancel.cancelInterviewId}
        onOpenChange={(open) => {
          if (!open) cancel.closeCancel();
        }}
        isPending={cancel.isPending}
        isGroupInterview={cancel.isGroupInterview}
        onConfirm={cancel.confirmCancel}
        onCancel={cancel.closeCancel}
      />
    </div>
  );
}
